import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTemplates } from '../context/TemplateContext';
import { useInstances } from '../context/InstanceContext';
import { useCourses } from '../context/CourseContext';
import { ConfirmDialog } from '../components/ui';
import ViewLayout2 from '../components/ui/ViewLayout2';
import TemplateMetadataBar2 from '../components/template/TemplateMetadataBar2';
import { normalizeQuestion } from '../utils/llm-generator/questionTransformers';
import { Logger } from '../utils/logger';
import { notifyError, notifyWarning } from '../services/notificationService';

const log = Logger.create('TemplateEditor');

// Tab Components (reuse existing ones)
import QuestionsTab from '../components/template/QuestionsTab';
import SettingsTab from '../components/template/SettingsTab';
// import PreviewTab from '../components/template/PreviewTab'; // Preview tab disabled
import ImportModal from '../components/template/ImportModal';
import MetadataModal from '../components/template/MetadataModal';

/**
 * TemplateEditorView2 - Neue Template Editor View mit Unified Design System
 * 
 * Struktur:
 * - Header mit Zurück-Button und Titel
 * - Action Bar mit Tabs (Fragen, Einstellungen, Vorschau)
 * - TemplateMetadataBar2 für Name, Kurs, Beschreibung
 * - Tab Content je nach aktivem Tab
 */

const TABS = [
  { id: 'questions', label: 'Fragen' },
  { id: 'settings', label: 'Einstellungen' }
  // { id: 'preview', label: 'Vorschau' } // Preview tab disabled
];

const DEFAULT_SETTINGS = {
  displayName: '',
  showIntroScreen: true,
  anonymousMode: false,
  enableGamification: true,
  basePointsPerQuestion: 100,
  enableTimeBonus: true,
  enableStreakBonus: true,
  streakBonusPoints: 50,
  enableQuestionTimer: false,
  questionTimerSeconds: 30,
  showLeaderboard: true,
  showStreak: true,
  lockOnReveal: true,
  enableJokers: false,
  enableJoker5050: true,
  enableJokerAudience: true,
  enableJokerBestPlayer: true,
  enableJokerTime: true,
  // Multi-Answer Mode: 'auto' (based on question), 'always' (always allow multiple), 'never' (single choice only)
  multiAnswerMode: 'auto',
};

export default function TemplateEditorView2() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const courseIdFromUrl = searchParams.get('courseId');
  
  // Check if coming back from LLM Generator with generated questions
  const generatedQuestionsFromLLM = location.state?.generatedQuestions;
  const fromLLMGenerator = location.state?.fromLLMGenerator;

  const { getTemplate, createTemplate, updateTemplate, deleteTemplate, templates } = useTemplates();
  const { startInstance } = useInstances();
  const { courses } = useCourses();

  const isNew = !templateId;

  // UI State
  const [activeTab, setActiveTab] = useState('questions');
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Template State
  const [templateName, setTemplateName] = useState('');
  const [courseId, setCourseId] = useState(courseIdFromUrl || null);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [questions, setQuestions] = useState([
    {
      question: '',
      options: ['', '', '', ''],
      correctAnswerIndices: [0] // Standard: Array of indices
    }
  ]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // Load existing template if editing
  useEffect(() => {
    if (templateId) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setTemplateName(template.name || '');
        setCourseId(template.courseId || null);
        setDescription(template.description || '');
        setTags(template.tags || []);
        // Normalize questions on load to ensure they use correctAnswerIndices
        const loadedQuestions = template.questions?.length > 0 
          ? template.questions.map(q => normalizeQuestion(q))
          : [{ question: '', options: ['', '', '', ''], correctAnswerIndices: [0] }];
        setQuestions(loadedQuestions);
        setSettings({ ...DEFAULT_SETTINGS, ...template.settings });
        setIsInitialLoad(false);
      }
    } else {
      setIsInitialLoad(false);
    }
  }, [templateId, templates]);

  // Handle questions from LLM Generator
  useEffect(() => {
    if (fromLLMGenerator && generatedQuestionsFromLLM?.length > 0) {
      // Normalize and add generated questions
      const normalizedQuestions = generatedQuestionsFromLLM.map(q => normalizeQuestion(q));
      setQuestions(prev => {
        // If only empty default question exists, replace it
        if (prev.length === 1 && !prev[0].question?.trim()) {
          return normalizedQuestions;
        }
        // Otherwise append to existing questions
        return [...prev, ...normalizedQuestions];
      });
      setHasUnsavedChanges(true);
      // Clear location state to prevent re-adding on refresh
      window.history.replaceState({}, document.title);
      log.success(`${normalizedQuestions.length} Fragen vom LLM Generator hinzugefügt`);
    }
  }, [fromLLMGenerator, generatedQuestionsFromLLM]);

  // Mark as unsaved when changes occur
  useEffect(() => {
    if (!isInitialLoad) {
      setHasUnsavedChanges(true);
    }
  }, [templateName, courseId, description, tags, questions, settings]);

  // Warn on page leave with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Listen for delete event from SettingsTab
  useEffect(() => {
    const handleDeleteTemplate = async () => {
      if (!templateId) return;
      
      try {
        await deleteTemplate(templateId);
        navigate('/dashboard');
      } catch (error) {
        log.error('Error deleting template:', error);
        notifyError('Fehler beim Löschen des Templates');
      }
    };

    window.addEventListener('deleteTemplate', handleDeleteTemplate);
    return () => window.removeEventListener('deleteTemplate', handleDeleteTemplate);
  }, [templateId, deleteTemplate, navigate]);

  // Update tabs with question count
  const tabsWithCounts = useMemo(() => 
    TABS.map(tab => {
      if (tab.id === 'questions') return { ...tab, count: questions.length };
      return tab;
    }), 
    [questions.length]
  );

  // Get course name
  const courseName = useMemo(() => {
    const course = courses.find(c => c.id === courseId);
    return course?.name || null;
  }, [courses, courseId]);

  // Handlers
  const handleSave = async () => {
    if (!templateName.trim()) {
      notifyWarning('Bitte gib einen Vorlagennamen ein');
      return;
    }

    const validQuestions = questions.filter(q => q.question?.trim());
    if (validQuestions.length === 0) {
      notifyWarning('Bitte füge mindestens eine Frage hinzu');
      return;
    }

    // Normalize questions to ensure consistent correctAnswerIndices format
    const normalizedQuestions = validQuestions.map(q => normalizeQuestion(q));
    log.debug('Saving template with normalized questions:', normalizedQuestions);

    setIsSaving(true);
    try {
      const templateData = {
        name: templateName,
        courseId,
        description,
        tags,
        questions: normalizedQuestions,
        settings
      };

      if (templateId) {
        await updateTemplate(templateId, templateData);
      } else {
        const newTemplate = await createTemplate(templateData);
        navigate(`/template/${newTemplate.id}`, { replace: true });
      }

      setHasUnsavedChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      log.error('Error saving template:', error);
      notifyError('Fehler beim Speichern der Vorlage');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndStart = async () => {
    if (!templateName.trim()) {
      notifyWarning('Bitte gib einen Vorlagennamen ein');
      return;
    }

    const validQuestions = questions.filter(q => q.question?.trim());
    if (validQuestions.length === 0) {
      notifyWarning('Bitte füge mindestens eine Frage mit Text hinzu');
      return;
    }

    // Normalize questions to ensure consistent correctAnswerIndices format
    const normalizedQuestions = validQuestions.map(q => normalizeQuestion(q));

    setIsSaving(true);
    try {
      const templateData = {
        name: templateName,
        courseId,
        description,
        tags,
        questions: normalizedQuestions,
        settings
      };

      let finalTemplateId = templateId;

      if (templateId) {
        await updateTemplate(templateId, templateData);
      } else {
        const newTemplate = await createTemplate(templateData);
        finalTemplateId = newTemplate.id;
      }

      setHasUnsavedChanges(false);
      const instance = await startInstance(finalTemplateId);
      navigate(`/live/${instance.id}`);
    } catch (error) {
      log.error('Error saving/starting quiz:', error);
      notifyError('Fehler beim Speichern oder Starten des Quiz');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
    } else {
      navigateBack();
    }
  };

  const navigateBack = () => {
    if (courseId) {
      navigate(`/course/${courseId}`);
    } else {
      navigate('/dashboard');
    }
  };

  const handleAIGenerate = () => {
    const state = {
      returnTo: templateId ? `/template/${templateId}` : `/template/new${courseId ? `?courseId=${courseId}` : ''}`,
      templateId,
      courseId,
      templateName,
      existingQuestions: questions
    };
    navigate('/llm-generator', { state });
  };

  const handleImport = () => {
    setShowImportModal(true);
  };

  const handleImportQuestions = (importedQuestions, mode) => {
    if (mode === 'add') {
      setQuestions([...questions, ...importedQuestions]);
    } else {
      setQuestions(importedQuestions);
    }
    setShowImportModal(false);
  };

  const handleImportTemplate = (templateData) => {
    if (confirm('Komplette Vorlage importieren? Dies überschreibt alle aktuellen Einstellungen.')) {
      if (templateData.name) setTemplateName(templateData.name);
      if (templateData.description) setDescription(templateData.description);
      if (templateData.tags) setTags(templateData.tags);
      if (templateData.questions) setQuestions(templateData.questions);
      if (templateData.settings) setSettings({ ...DEFAULT_SETTINGS, ...templateData.settings });
      setShowImportModal(false);
    }
  };

  const handleMetadataChange = (data) => {
    if (data.name !== undefined) setTemplateName(data.name);
    if (data.courseId !== undefined) setCourseId(data.courseId);
    if (data.description !== undefined) setDescription(data.description);
    if (data.tags !== undefined) setTags(data.tags);
  };

  return (
    <ViewLayout2
      title={isNew ? 'Neue Vorlage' : 'Vorlage bearbeiten'}
      onBack={handleCancel}
      tabs={tabsWithCounts}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      primaryAction={{
        label: 'Quiz starten',
        onClick: handleSaveAndStart,
        disabled: isSaving || questions.filter(q => q.question?.trim()).length === 0
      }}
      secondaryActions={[
        { label: 'Speichern', onClick: handleSave, disabled: isSaving, variant: 'success' }
      ]}
    >
      {/* Save Success Notification */}
      {saveSuccess && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-fade-in">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-medium">Vorlage erfolgreich gespeichert!</span>
        </div>
      )}

      {/* Template Metadata Bar */}
      <TemplateMetadataBar2
        name={templateName}
        courseName={courseName}
        description={description}
        hasUnsavedChanges={hasUnsavedChanges}
        onEdit={() => setShowMetadataModal(true)}
      />

      {/* Tab Content */}
      {activeTab === 'questions' && (
        <QuestionsTab
          questions={questions}
          onQuestionsChange={setQuestions}
          onAIGenerate={handleAIGenerate}
          onImport={handleImport}
        />
      )}

      {activeTab === 'settings' && (
        <SettingsTab
          settings={settings}
          onSettingsChange={setSettings}
        />
      )}

      {/* Preview tab disabled - was here before removal */}
      
      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onImportQuestions={handleImportQuestions}
          onImportTemplate={handleImportTemplate}
        />
      )}

      {/* Metadata Modal */}
      {showMetadataModal && (
        <MetadataModal
          name={templateName}
          courseId={courseId}
          description={description}
          tags={tags}
          courses={courses}
          onSave={(data) => {
            handleMetadataChange(data);
            setShowMetadataModal(false);
          }}
          onClose={() => setShowMetadataModal(false)}
        />
      )}

      {/* Cancel Confirmation */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => {
          setShowCancelConfirm(false);
          navigateBack();
        }}
        title="Ungespeicherte Änderungen"
        message="Du hast ungespeicherte Änderungen. Möchtest du wirklich fortfahren?"
        confirmLabel="Fortfahren"
        cancelLabel="Abbrechen"
        variant="warning"
      />
    </ViewLayout2>
  );
}
